import React, { createContext, useState, useEffect, useContext, useCallback } from "react"
import { firebaseAuth } from "../firebaseConfig"
import AuthContext from "./AuthContext"
import { API_BASE_URL } from "../api/common"

const FavoritesContext = createContext()

export const FavoritesProvider = ({ children }) => {
  const { user } = useContext(AuthContext)
  const [favorites, setFavorites] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Fetch favorites from API once
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user || !user.uid) {
        // Load from localStorage if not logged in
        if (typeof window !== "undefined") {
          const localFavorites = JSON.parse(localStorage.getItem("favorites") || "[]")
          setFavorites(localFavorites)
        }
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const token = await firebaseAuth.currentUser.getIdToken()
        const response = await fetch(
          `${API_BASE_URL}/user/${user.uid}/favorite-spaces`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        )

        if (response.ok) {
          const data = await response.json()
          const userFavoriteSpaces = data.userFavoriteSpaces || []
          const favoritesFromAPI = userFavoriteSpaces.map(space => space.spaceId)

          setFavorites(favoritesFromAPI)

          // Sync with localStorage
          if (typeof window !== "undefined") {
            localStorage.setItem("favorites", JSON.stringify(favoritesFromAPI))
          }
          setError(null)
        } else {
          throw new Error("Failed to fetch favorites")
        }
      } catch (err) {
        console.error("Error fetching favorites:", err)
        setError(err.message)

        // Fallback to localStorage
        if (typeof window !== "undefined") {
          const localFavorites = JSON.parse(localStorage.getItem("favorites") || "[]")
          setFavorites(localFavorites)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchFavorites()
  }, [user?.uid])

  // Toggle favorite
  const toggleFavorite = useCallback(async (spaceId) => {
    if (!user || !user.uid) {
      return { success: false, error: "User not logged in" }
    }

    const isFavorited = favorites.includes(spaceId)
    const newFavorites = isFavorited
      ? favorites.filter(id => id !== spaceId)
      : [...favorites, spaceId]

    // Optimistically update state
    setFavorites(newFavorites)

    // Update localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("favorites", JSON.stringify(newFavorites))
    }

    try {
      const token = await firebaseAuth.currentUser.getIdToken()
      const response = await fetch(
        `${API_BASE_URL}/user/${user.uid}/favorite-spaces`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ spaceId }),
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to update favorites: ${response.status}`)
      }

      return { success: true }
    } catch (err) {
      console.error("Error toggling favorite:", err)

      // Revert on error
      setFavorites(favorites)
      if (typeof window !== "undefined") {
        localStorage.setItem("favorites", JSON.stringify(favorites))
      }

      return { success: false, error: err.message }
    }
  }, [user, favorites])

  // Check if a space is favorited
  const isFavorited = useCallback((spaceId) => {
    return favorites.includes(spaceId)
  }, [favorites])

  const value = {
    favorites,
    loading,
    error,
    toggleFavorite,
    isFavorited,
  }

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}

export default FavoritesContext
